import type { AIProvider } from './apiKeyService';
import { getCustomBaseUrl } from './apiKeyService';

export interface ModelFetchResult {
  models: string[];
  error?: string;
}

const dedupeSort = (arr: string[]) => [...new Set(arr.filter(Boolean))].sort();

/**
 * Fetch the list of available model ids for a provider using the user's saved key.
 * Runs client-side (same as generation). Never throws — returns { models, error };
 * callers fall back to a free-text model id when `error` is set (e.g. CORS).
 */
export async function fetchProviderModels(
  provider: AIProvider,
  apiKey: string
): Promise<ModelFetchResult> {
  try {
    if (provider === 'gemini') {
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models?pageSize=200&key=${encodeURIComponent(apiKey)}`
      );
      if (!res.ok) return { models: [], error: `Gemini returned ${res.status}` };
      const data = await res.json();
      const models = ((data.models as Array<{ name?: string; supportedGenerationMethods?: string[] }>) || [])
        .filter((m) => (m.supportedGenerationMethods || []).includes('generateContent'))
        .map((m) => (m.name || '').replace(/^models\//, ''));
      return { models: dedupeSort(models) };
    }

    if (provider === 'openai') {
      const res = await fetch('https://api.openai.com/v1/models', {
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      if (!res.ok) return { models: [], error: `OpenAI returned ${res.status}` };
      const data = await res.json();
      const models = ((data.data as Array<{ id?: string }>) || [])
        .map((m) => m.id || '')
        .filter((id) => /^(gpt-|o\d|chatgpt-)/.test(id));
      return { models: dedupeSort(models) };
    }

    if (provider === 'anthropic') {
      const res = await fetch('https://api.anthropic.com/v1/models?limit=100', {
        headers: {
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
      });
      if (!res.ok) return { models: [], error: `Anthropic returned ${res.status}` };
      const data = await res.json();
      const models = ((data.data as Array<{ id?: string }>) || []).map((m) => m.id || '');
      return { models: dedupeSort(models) };
    }

    // custom / OpenRouter — any OpenAI-compatible `/models` endpoint.
    const base = getCustomBaseUrl().replace(/\/+$/, '');
    const res = await fetch(`${base}/models`, {
      headers: { Authorization: `Bearer ${apiKey}` },
    });
    if (!res.ok) return { models: [], error: `Model list returned ${res.status}` };
    const data = await res.json();
    const models = ((data.data as Array<{ id?: string }>) || []).map((m) => m.id || '');
    return { models: dedupeSort(models) };
  } catch {
    return { models: [], error: 'Could not reach the provider (CORS or network).' };
  }
}
